import {
  Component,
  HostListener,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { Observable, Observer, forkJoin } from 'rxjs';

import { CanComponentDeactivate } from '../../guards/deactivate.guard';
import { TranslateService } from '@ngx-translate/core';
import { BaseContainer } from './base-container';
import { DeviceService } from 'src/app/services/device.service';

/**
 * Generic Form component that every form panel needs to extend.
 * This applies unsaved change detection when navigating away from the panel.
 */
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormComponent
  extends BaseContainer
  implements CanComponentDeactivate
{
  protected _confirmationService: ConfirmationService =
    inject(ConfirmationService);
  protected _translateService: TranslateService = inject(TranslateService);
  form?: UntypedFormGroup;

  constructor() {
    super();
  }

  /**
   * This handles in application unloads (route navigation).
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (!this.form || !this.form?.dirty) {
      return true;
    } else {
      return new Observable((observer: Observer<boolean>) => {
        const unsaved$ = this._translateService.get(
          'COMMON.MESSAGES.CONFIRMATION.UNSAVED_CHANGES'
        );
        const yes$ = this._translateService.get('COMMON.LABEL.BUTTONS.LEAVE');
        const no$ = this._translateService.get('COMMON.LABEL.BUTTONS.CANCEL');
        const header$ = this._translateService.get(
          'COMMON.LABEL.HEADERS.LEAVE_PAGE'
        );
        forkJoin(unsaved$, yes$, no$, header$).subscribe((messages) => {
          this._confirmationService.confirm({
            header: messages[3],
            icon: 'fa fa-question-circle',
            acceptVisible: true,
            rejectVisible: true,
            acceptLabel: messages[1],
            rejectLabel: messages[2],
            message: messages[0],
            accept: () => {
              observer.next(true);
              observer.complete();
            },
            reject: () => {
              observer.next(false);
              observer.complete();
            },
          });
        });
      });
    }
  }

  /**
   * This handles external unload events (refresh, tab close, etc).
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (!this.form?.dirty) {
      return true;
    } else {
      // This doesn't use the observable pattern above because the warning is a browser dialog.
      return false;
    }
  }
}
